import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import { DoohReportErrorCode } from '@/Enums/ReportingDashboard/DoohReportErrorCode.enum';
import { DOOH_CAMPAIGNS_ENDPOINT, DOOH_REPORT_ENDPOINT } from '@/constants/endpoints';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import {
  createArrayWithDate,
  createArrayWithField,
  createCampainsListWithAdvertiser,
  createDayOfWeekArray,
  createPublisherArray,
  mapFilter,
} from '@/helpers/mappingForNeuron';
import { getEffectiveFilters } from '@/helpers/reportingDashboard/filters';
import {
  selectReportingDashboardCurrentApiTabClients,
  selectReportingDashboardCurrentApiTabWhitelabels,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  dateData: [],
  campaignNameData: [],
  creativeData: [],
  placementData: [],
  publisherNameData: [],
  dayOfWeekData: [],
  campaignsList: [],
  advertiserData: [],
  venueTypeData: [],
  campaignsListRequestId: DEFAULT_LOADING_REQUEST_ID,
  reportDataRequestId: DEFAULT_LOADING_REQUEST_ID,
  dayTimeDataRequestId: DEFAULT_LOADING_REQUEST_ID,

  errorCardMessage: '',
};

export const reportingDashboardNeuron = createSlice({
  name: 'reportingDashboardNeuron',
  initialState,
  reducers: {
    resetNeuronReportsLoadingState: state => {
      Object.keys(state).forEach(key => {
        if (key.endsWith('RequestId')) {
          state[key] = DEFAULT_LOADING_REQUEST_ID;
        }
      });
      state.errorCardMessage = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getNeuronReportData.pending, (state, action) => {
        state.reportDataRequestId = action.meta.requestId;
        state.errorCardMessage = '';
      })
      .addCase(getNeuronReportData.rejected, (state, action) => {
        if (state.reportDataRequestId === action.meta.requestId) {
          state.reportDataRequestId = false;
          state.errorCardMessage = Object.values(DoohReportErrorCode).includes(action.error?.code)
            ? action.error.message
            : 'There seems to be a momentary issue loading your data. Please retry.';
        }
      })
      .addCase(getNeuronReportData.fulfilled, (state, action) => {
        if (state.reportDataRequestId === action?.meta?.requestId) {
          const errorCardMessage =
            action.payload?.campaignNameData?.length === 0
              ? "We couldn't find any data. Please adjust your filters and try again."
              : '';
          state.errorCardMessage = errorCardMessage;
          state.reportDataRequestId = false;
          state.dateData = action.payload.daysData;
          state.campaignNameData = action.payload.campaignNameData;
          state.creativeData = action.payload.creativeData;
          state.placementData = action.payload.placementData;
          state.publisherNameData = action.payload.publisherNameData;
          state.dayOfWeekData = action.payload.dayOfWeekData;
          state.venueTypeData = action.payload.venueTypeData;
          state.advertiserData = action.payload.advertiserData;
        }
      })
      // .addCase(getNeuronByDaypartData.pending, (state, action) => {
      //   state.dayTimeDataRequestId = action.meta.requestId;
      // })
      // .addCase(getNeuronByDaypartData.rejected, (state, action) => {
      //   if (state.dayTimeDataRequestId === action.meta.requestId) {
      //     state.dayTimeDataRequestId = true;
      //   }
      // })
      // .addCase(getNeuronByDaypartData.fulfilled, (state, action) => {
      //   if (state.dayTimeDataRequestId === action?.meta?.requestId) {
      //     state.dayTimeDataRequestId = false;
      //     state.dayTimeData = action.payload;
      //   }
      // })
      .addCase(getNeuronCampainsData.pending, (state, action) => {
        state.campaignsListRequestId = action.meta.requestId;
      })
      .addCase(getNeuronCampainsData.rejected, (state, action) => {
        if (state.campaignsListRequestId === action.meta.requestId) {
          state.campaignsListRequestId = false;
        }
      })
      .addCase(getNeuronCampainsData.fulfilled, (state, action) => {
        if (state.campaignsListRequestId === action?.meta?.requestId) {
          state.campaignsListRequestId = false;
          state.campaignsList = action.payload;
        }
      });
  },
});

export const getNeuronReportData = createAsyncThunk(
  'reportingDashboardNeuronSlice/getNeuronReportData',
  async (payload, { getState }) => {
    try {
      const state = getState();
      const {
        me: { role },
      } = state;

      const filters = getEffectiveFilters(state, ApiTabIndex.Dooh);
      const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
      const clients = selectReportingDashboardCurrentApiTabClients(state);

      const { appliedFilter, selectedPeriods } = mapFilter(filters, role, clients, whitelabels);
      if (!(appliedFilter.advertiserIds.length || appliedFilter.campaignIds.length)) {
        return {
          daysData: [],
          campaignNameData: [],
          creativeData: [],
          publisherNameData: [],
          placementData: [],
          dayOfWeekData: [],
          venueTypeData: [],
          advertiserData: [],
        };
      }

      const { data: responseData } = await axios.post(DOOH_REPORT_ENDPOINT, appliedFilter, {
        ...payload,
      });
      const impressionsByDate = responseData.map(item => item.data).flat();
      const daysData = createArrayWithDate(impressionsByDate, selectedPeriods);
      const dayOfWeekData = createDayOfWeekArray(daysData);
      const advertiserData = createArrayWithField(responseData);
      const campaignNameData = createArrayWithField(responseData, 'campaignName', 'Campaign Name');
      const creativeData = createArrayWithField(responseData, 'creativeName', 'Creative Name');
      // TODO: move to api
      const publisherNameData = createPublisherArray(responseData, []);
      const placementData = createArrayWithField(responseData, 'lineItemName', 'Line Item Name');
      const venueTypeData = createArrayWithField(responseData, 'venueTypeName', 'Venue Type');

      payload?.onSuccess?.();
      return {
        daysData,
        campaignNameData,
        creativeData,
        advertiserData,
        publisherNameData,
        placementData,
        dayOfWeekData,
        venueTypeData,
      };
    } catch (err) {
      console.error(err);
      throw err?.response?.data;
    }
  },
);

export const getNeuronCampainsData = createAsyncThunk(
  'reportingDashboardNeuronSlice/getNeuronCampainsData',
  async payload => {
    try {
      const responseQuery = await axios.get(DOOH_CAMPAIGNS_ENDPOINT, {
        ...payload,
      });

      payload?.onSuccess?.();
      return createCampainsListWithAdvertiser(responseQuery.data);
    } catch (err) {
      throw err?.response?.data;
    }
  },
);

export const { resetNeuronReportsLoadingState } = reportingDashboardNeuron.actions;

export default reportingDashboardNeuron.reducer;
